import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Pencil, Check } from 'lucide-react';
import { api } from '@/utils/api';

// S2-assets (Brian 2026-09-29 model): company custom asset types manager.
// One source of truth for the picker lives at GET /api/assets/types; this
// modal manages only the company's custom types (built-ins are read-only).
// Server enforces the write roles — this UI hides what the server 403s.

interface TypeRow {
  id: number;
  name: string;
  family: string;
  defaultCadence: string;
  builtin: boolean;
  isActive?: boolean;
}

const FAMILY_OPTIONS = [
  ['PPE_HARNESS', 'PPE Harness'], ['PPE_HELMET', 'PPE Helmet'], ['PPE_GLOVES', 'PPE Gloves'],
  ['PPE_BOOTS', 'PPE Boots'], ['FALL_PROTECTION', 'Fall Protection'],
  ['RIGGING_SLING', 'Rigging Sling'], ['RIGGING_SHACKLE', 'Rigging Shackle'], ['RIGGING_HOIST', 'Rigging Hoist'],
  ['TEST_EQUIPMENT', 'Test Equipment'], ['VEHICLE', 'Vehicle'], ['TOOL', 'Tool'], ['OTHER', 'Other'],
] as const;

const CADENCE_OPTIONS = [
  ['NONE', 'No Regular Inspection'], ['DAILY', 'Daily'], ['WEEKLY', 'Weekly'], ['MONTHLY', 'Monthly'],
  ['QUARTERLY', 'Quarterly (Every 3 months)'], ['SEMI_ANNUAL', 'Semi-Annual (Every 6 months)'], ['ANNUAL', 'Annual (Yearly)'],
] as const;

interface AssetTypeManagerModalProps {
  onClose: () => void;
}

export function AssetTypeManagerModal({ onClose }: AssetTypeManagerModalProps) {
  const [types, setTypes] = useState<TypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editCadence, setEditCadence] = useState('NONE');
  const [newName, setNewName] = useState('');
  const [newFamily, setNewFamily] = useState<string>('TOOL');
  const [newCadence, setNewCadence] = useState<string>('NONE');

  const loadTypes = async () => {
    try {
      setLoading(true);
      // includeInactive: the manager view shows deactivated types too, so the
      // restore path named in the server's delete message actually exists here.
      const res = await api.get('/assets/types', { params: { includeInactive: '1' } });
      if (res.data.success) {
        setTypes((res.data.data ?? []).filter((t: TypeRow) => !t.builtin));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load asset types — check your connection and try again');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await api.post('/assets/types', {
        name: newName.trim(),
        family: newFamily,
        defaultCadence: newCadence,
      });
      if (res.data.success) {
        setNotice(`“${res.data.data.name}” added — it now appears in the asset type picker.`);
        setNewName('');
        await loadTypes();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not create the asset type — check the name and try again');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (t: TypeRow) => {
    setEditingId(t.id);
    setEditName(t.name);
    setEditCadence(t.defaultCadence);
  };

  const handleSaveEdit = async () => {
    if (editingId == null) return;
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await api.patch(`/assets/types/${editingId}`, {
        name: editName.trim(),
        defaultCadence: editCadence,
      });
      if (res.data.success) {
        setNotice(`Saved — “${res.data.data.name}” updated.`);
        setEditingId(null);
        await loadTypes();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save the change — try again');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (t: TypeRow) => {
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await api.patch(`/assets/types/${t.id}`, { active: true });
      if (res.data.success) {
        setNotice(`“${res.data.data.name}” restored — it is back in the asset type picker.`);
        await loadTypes();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not restore the asset type — try again');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t: TypeRow) => {
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await api.delete(`/assets/types/${t.id}`);
      if (res.data.success) {
        // The server decides hard vs soft delete and says why — show its
        // message verbatim (never claim a delete that only deactivated).
        setNotice(res.data.message);
        await loadTypes();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not delete the asset type — try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Manage Asset Types</h2>
            <p className="text-sm text-gray-500 mt-1">
              Company types appear in the asset form above the built-in types. Built-in types cannot be changed.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>
          )}
          {notice && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">{notice}</div>
          )}

          {/* Create */}
          <form onSubmit={handleCreate} className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Add a New Asset Type</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Genie Lift"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Behavior Family</label>
                <select
                  value={newFamily}
                  onChange={(e) => setNewFamily(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {FAMILY_OPTIONS.map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Default Inspection</label>
                <select
                  value={newCadence}
                  onChange={(e) => setNewCadence(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {CADENCE_OPTIONS.map(([v, label]) => (
                    <option key={v} value={v}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              The family drives behavior (PPE → JHA check-off sync, Test Equipment → calibration, Vehicle → DOT).
            </p>
            <button
              type="submit"
              disabled={saving || !newName.trim()}
              className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="h-4 w-4" /> Add Type
            </button>
          </form>

          {/* List */}
          {loading ? (
            <p className="text-sm text-gray-500">Loading types…</p>
          ) : types.length === 0 ? (
            <p className="text-sm text-gray-500">
              No company types yet — add one above (e.g. “SRL Lanyard”, “Capstan Winch”, “Spreader Bar”).
            </p>
          ) : (
            <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
              {types.map((t) => (
                <li key={t.id} className={`p-3 flex items-center gap-3 ${t.isActive === false ? 'opacity-60' : ''}`}>
                  {editingId === t.id ? (
                    <>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded"
                      />
                      <select
                        value={editCadence}
                        onChange={(e) => setEditCadence(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded"
                      >
                        {CADENCE_OPTIONS.map(([v, label]) => (
                          <option key={v} value={v}>{label}</option>
                        ))}
                      </select>
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving || !editName.trim()}
                        className="p-2 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                        title="Save"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {t.name}
                          {t.isActive === false && (
                            <span className="ml-2 text-xs font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                              Deactivated — hidden from pickers
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {FAMILY_OPTIONS.find(([v]) => v === t.family)?.[1] || t.family}
                          {' · '}
                          {CADENCE_OPTIONS.find(([v]) => v === t.defaultCadence)?.[1] || t.defaultCadence}
                        </p>
                      </div>
                      {t.isActive === false && (
                        <button
                          onClick={() => handleRestore(t)}
                          disabled={saving}
                          className="px-3 py-1.5 text-sm text-green-700 border border-green-300 hover:bg-green-50 rounded disabled:opacity-50"
                          title="Restore to the asset type picker"
                        >
                          Restore
                        </button>
                      )}
                      <button
                        onClick={() => startEdit(t)}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                        title="Rename / change default inspection"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(t)}
                        disabled={saving}
                        className="p-2 text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                        title="Delete (deactivates when still in use)"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-gray-500">
            Deleting a type that assets still use deactivates it instead — those assets keep their type history.
          </p>
        </div>
      </div>
    </div>
  );
}
